/**
 * Agentes — GEN-PX-022 (antes solo admin, GEN-PX-014). Botón del raíl para todos los perfiles; el kernel emite el
 * ítem `agentes` a todos (R-PT-02) y la vista se adapta a lo que el kernel da a quien mira:
 *  - Administrador (el kernel le emite `equipo`): la gestión de siempre, MiEquipoPanel (fichas, alta, pestaña Perfiles).
 *  - Resto (champion, usuario): SOLO los agentes que el kernel ya le da en la sesión (`agents_visibles`, los mismos
 *    de «Tu equipo de IA» de Inicio), con su ficha en solo lectura: foto, nombre, cargo, departamento y si lidera.
 *    Sin pestaña Perfiles y sin ningún dato más de la ficha (perfil, ámbito, instrucciones...), que sigue siendo admin:
 *    GET /v1/agentes y /v1/agentes/{id} dan 403 al no-admin (R-PT-04). No hay endpoint nuevo.
 * Cuando llegue el modelo de acceso por agente (GEN-TL-008), «los suyos» los decidirá ese modelo en el kernel.
 */

import { useState } from 'react'
import MiEquipoPanel from './MiEquipoPanel.jsx'
import TeamBoard from './TeamBoard.jsx'
import { useSession } from '../context/SessionContext.jsx'
import { useFotoGrandeAgente, useFotoAgente } from '../context/FotosAgentes.jsx'
import CabeceraLanding from './CabeceraLanding.jsx'
import { AgentAvatar } from './AgentAvatar.jsx'

const iniciales = nombre => (nombre ?? '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()

function TarjetaAgente({ agente, onAbrir }) {
  const fotoDe = useFotoGrandeAgente()
  const foto = fotoDe(agente)
  return (
    <button type="button" className="team-card team-card--dk" onClick={() => onAbrir(agente.id)}>
      <span className="team-card__photo">
        <span className="team-card__photo-ini">{iniciales(agente.nombre)}</span>
        {foto && <img src={foto} alt="" decoding="async" onError={e => { e.currentTarget.style.display = 'none' }} />}
      </span>
      <span className="team-card__body">
        <span className="team-card__name">{agente.nombre}</span>
        {agente.rol && <span className="team-card__role">{agente.rol}</span>}
        {agente.lidera === true && <span className="team-card__lidera">Lidera</span>}
      </span>
    </button>
  )
}

function FichaLectura({ agente, onVolver, onHablar }) {
  const fotoDe = useFotoGrandeAgente()
  const foto = fotoDe(agente)
  return (
    <section className="mec-visor">
      <button type="button" className="ficha-volver" onClick={onVolver}>← Agentes</button>
      <div className="ficha-lectura">
        <div className="ficha-lectura__foto">
          {foto ? <img src={foto} alt={`Foto de ${agente.nombre}`} /> : <AgentAvatar name={agente.nombre} avatarUrl={null} size={160} />}
        </div>
        <div className="ficha-lectura__tarjetas">
          <section className="ficha-card ficha-card--cabecera">
            <h2>{agente.nombre}</h2>
            {agente.departamento && <span className="equipo-card__depto">{agente.departamento}</span>}
            {agente.rol?.trim() && <span className="ficha-card__cargo">{agente.rol}</span>}
            {agente.lidera === true && (
              <span className="equipo-card__chips"><span className="equipo-chip equipo-chip--lidera">Lidera</span></span>
            )}
          </section>
          {onHablar && (
            <div className="ficha-form__acciones">
              <button type="button" className="btn btn--ghost" onClick={() => onHablar(agente)}>Hablar con {agente.nombre}</button>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function AgentesDeLaSesion({ onAbrirAgente, agenteInicial = null }) {
  const { agents } = useSession()
  const [abierto, setAbierto] = useState(agenteInicial)
  // Los mismos que «Tu equipo de IA» de Inicio: los operativos.
  const disponibles = (agents ?? []).filter(a => a.puede_iniciar_chat !== false)
  const agente = disponibles.find(a => a.id === abierto)
  if (agente) return <FichaLectura agente={agente} onVolver={() => setAbierto(null)} onHablar={onAbrirAgente} />
  return (
    <section className="mec-visor">
      <h2>Agentes</h2>
      {disponibles.length === 0 && <p className="equipo-estado">Todavía no tienes agentes a tu disposición.</p>}
      <TeamBoard
        agents={disponibles}
        ariaLabel="Tus agentes"
        renderCard={a => <TarjetaAgente key={a.id} agente={a} onAbrir={setAbierto} />}
      />
    </section>
  )
}

export default function AgentesPanel({ onAbrirAgente, subId = null }) {
  const { capabilities, agents = [] } = useSession()
  const fotoDe = useFotoAgente()
  // GEN-PX-027: la cifra de la cabecera (un agente nuevo) abre su ficha; se remonta la vista con `key` para abrirla.
  const [destino, setDestino] = useState({ clave: 0, id: null })
  const gestiona = !!capabilities?.menu?.some(m => m.id === 'equipo' && m.visible && m.enabled)
  // Cabecera de Jorge que emite el kernel en el ítem `agentes`; aquí solo se pinta.
  const cab = capabilities?.menu?.find(m => m?.id === 'agentes')?.cabecera
  const ag = cab?.agente && (agents.find(a => a.id === cab.agente.id) ?? cab.agente)
  const cifras = (cab?.cifras ?? []).map(c => ({ texto: c.texto, onClick: () => setDestino(d => ({ clave: d.clave + 1, id: c.seccion })) }))
  return (
    <>
      {cab && (
        <CabeceraLanding
          agente={ag ? { nombre: cab.agente.nombre, rol: cab.agente.rol, foto: fotoDe(ag) } : null}
          titulo={cab.titulo}
          texto={cab.texto}
          cifras={cifras}
        />
      )}
      {gestiona
        ? <MiEquipoPanel key={destino.clave} subId={subId} agenteInicial={destino.id} />
        : <AgentesDeLaSesion key={destino.clave} onAbrirAgente={onAbrirAgente} agenteInicial={destino.id} />}
    </>
  )
}
